<template>
  <div>
    <header class="pb-8 px-24">
      <div class="flex mt-16 space-x-8">
        <div class="flex-1">
          <div class="mb-4">
            <img v-if="account.logo" :src="account.logo" class="w-24" />
          </div>
          <h1 class="text-lg text-gray-800">
            <span class="font-bold text-blue-500"> INVOICE: {{ invoice.number || '00000' }}</span>
          </h1>
          <h2 class="text-base text-gray-800">
            <span class="font-bold"> Issue date: {{ issueDate }}</span>
          </h2>
        </div>
        <div class="flex-1">
          <div>
            <div class="text-right text-sm font-bold mb-4">
              {{ invoice.issuerName || '-' }}
            </div>
            <div class="text-right text-xs">
              {{ invoice.issuerBusiness || '-' }}
            </div>
            <div class="text-right text-xs">
              {{ invoice.issuerAddress || '-' }}
            </div>
            <div class="text-right text-xs">
              {{ invoice.issuerVatNumber || '-' }}
            </div>
            <div class="text-right text-xs">
              {{ invoice.issuerVatOffice || '-' }}
            </div>
          </div>
        </div>
      </div>

      <div class="flex mt-8 space-x-8">
        <div class="w-1/2">
          <div>
            <div class="text-lg font-bold mb-4">
              {{ invoice.recipientName || '-' }}
            </div>
            <div class="text-xs">
              {{ invoice.recipientBusiness || '-' }}
            </div>
            <div class="text-xs">
              {{ invoice.recipientAddress || '-' }}
            </div>
            <div class="text-xs">Vat number: {{ invoice.recipientVatNumber || '-' }}</div>
            <div class="text-xs">
              {{ invoice.recipientVatOffice || '-' }}
            </div>
          </div>
        </div>
      </div>
    </header>

    <main>
      <div class="px-20 py-8">
        <table class="table-auto w-full border-collapse">
          <thead>
            <tr class="border-b-2 border-blue-500 text-blue-500 text-xxs uppercase tracking-wider">
              <th class="text-center pb-2 px-4 w-px whitespace-nowrap">#</th>
              <th class="w-full text-left pb-2">Description</th>
              <th class="text-right pb-2 pr-4">Qty</th>
              <th class="text-right pb-2 pr-4">Price / Unit</th>
              <th class="text-right pb-2 pr-4">Net Amount</th>
              <th class="text-right pb-2 pr-4">Vat Rate</th>
              <th class="text-right pb-2 pr-4">Vat Amount</th>
              <th class="text-right pb-2 pr-4">Total Amount</th>
            </tr>
          </thead>

          <InvoiceLine
            v-for="(invoiceLine, idx) in invoiceLines"
            :key="idx"
            :line-number="idx + 1"
            :invoice-line="invoiceLine"
            :invoice-lines="[]"
          />
          <tfoot>
            <tr class="text-xs">
              <td class="border-t-2 border-blue-500 pt-2" colspan="6" />
              <td class="border-t-2 border-blue-500 text-right pt-2 whitespace-nowrap">
                Sub total
              </td>
              <td class="border-t-2 border-blue-500 text-right pt-2 px-4 whitespace-nowrap">
                {{ totalNetAmountHumanReadable }}
              </td>
            </tr>
            <tr class="text-xs">
              <td colspan="6" />
              <td class="text-right whitespace-nowrap">
                <template v-if="vatRate === 0"> * </template>VAT (0%)
              </td>
              <td class="text-right px-4 whitespace-nowrap">
                {{ totalVatAmountHumanReadable }}
              </td>
            </tr>
            <tr class="text-xs font-bold">
              <td colspan="6" />
              <td class="text-right whitespace-nowrap">Total</td>
              <td class="text-right px-4 whitespace-nowrap">
                {{ totalHumanReadable }}
              </td>
            </tr>
          </tfoot>
        </table>
      </div>
    </main>

    <footer class="px-24">
      <table class="auto w-full">
        <tbody>
          <tr>
            <td class="align-top">
              <div class="flex-1 text-xxs">
                <h3 class="text-sm font-bold text-blue-600">
                  Thank you for using {{ invoice.issuerName }}.
                </h3>
              </div>
            </td>
            <td class="align-top">
              <!-- <div class="flex-1 text-xxs">
                <h5
                  class="text-xxs uppercase font-bold tracking-widest
                  text-blue-500 text-right mb-2"
                >
                  ΑΠΟΔΟΧΗ ΠΡΟΣΦΟΡΑΣ
                </h5>
                <div class="pt-16 text-right text-xxs">
                  <span>Σφραγίδα - Υπογραφή</span>
                </div>
              </div> -->
            </td>
          </tr>
        </tbody>
      </table>
      <div class="text-center text-xs mt-16">
        <template v-if="paymentAccountNumbers.length > 0">
          <div class="uppercase font-bold tracking-widest text-blue-500 mb-1">
            ΤΡΑΠΕΖΙΚΟΙ ΛΟΓΑΡΙΑΣΜΟΙ
          </div>
          <span class="uppercase tracking-widest">{{ concatenatedPaymentAccountNumbers }}</span>
        </template>
        <p v-if="vatRate === 0" class="mt-2">
          * VAT zero-rated: Customer to account for any VAT arising on this supply in accordance
          with Article 196, Council Directive 2006/112/EC
        </p>
      </div>
    </footer>
  </div>
</template>

<script>
import moment from 'moment';
import Numbro from '@/utils/numbro';
import InvoiceLine from './printableInvoice/InvoiceLine.vue';

const invoiceLineNetAmount = (invoiceLine) => invoiceLine.quantity * invoiceLine.pricePerUnit;

const invoiceLineVatAmount = (invoiceLine) =>
  invoiceLine.vatRate * invoiceLineNetAmount(invoiceLine);

export default {
  components: {
    InvoiceLine,
  },
  props: {
    invoice: {
      type: Object,
      required: true,
    },
    accountRelation: {
      type: Object,
      required: true,
    },
  },
  computed: {
    account() {
      return this.$store.state.entities.accounts.byId[this.accountRelation.account_uuid] || null;
    },
    paymentAccounts() {
      if (!this.account?.paymentAccounts || !Array.isArray(this.account.paymentAccounts)) {
        return [];
      }

      return this.account.paymentAccounts.map(
        (id) => this.$store.state.entities.accountPaymentAccounts.byId[id]
      );
    },
    paymentAccountNumbers() {
      return this.paymentAccounts.map((p) => p.attributes.number).filter((x) => x);
    },
    concatenatedPaymentAccountNumbers() {
      return this.paymentAccountNumbers.join(' | ');
    },
    invoiceLines() {
      return this.invoice?.invoiceLines || [];
    },
    rootInvoiceLines() {
      return this.invoiceLines.filter((invoiceLine) =>
        ['parentInvoiceLineId', '_parentInvoiceLineId'].some((key) => invoiceLine[key] === null)
      );
    },
    childInvoiceLines() {
      return this.invoiceLines.filter((invoiceLine) =>
        ['parentInvoiceLineId', '_parentInvoiceLineId'].some((key) => invoiceLine[key] !== null)
      );
    },
    issueDate() {
      return moment(this.invoice.issueDate).format('DD-MM-YYYY');
    },
    vatRate() {
      return this.accountRelation?.defaultVatRate || 0;
    },
    vatRateHumanReadable() {
      return Numbro(this.vatRate).format({
        output: 'percent',
        mantissa: 2,
        spaceSeparated: true,
      });
    },
    totalVatAmount() {
      return this.invoiceLines.reduce(
        (total, invoiceLine) => total + invoiceLineVatAmount(invoiceLine),
        0
      );
    },
    totalVatAmountHumanReadable() {
      return Numbro(this.totalVatAmount).formatCurrency({
        thousandSeparated: true,
        mantissa: 2,
        spaceSeparated: true,
      });
    },
    totalNetAmount() {
      return this.invoiceLines.reduce(
        (total, invoiceLine) => total + invoiceLineNetAmount(invoiceLine),
        0
      );
    },
    totalNetAmountHumanReadable() {
      return Numbro(this.totalNetAmount).formatCurrency({
        thousandSeparated: true,
        mantissa: 2,
        spaceSeparated: true,
      });
    },
    total() {
      return this.totalNetAmount + this.totalVatAmount;
    },
    totalHumanReadable() {
      return Numbro(this.total).formatCurrency({
        thousandSeparated: true,
        mantissa: 2,
        spaceSeparated: true,
      });
    },
  },
};
</script>
